/* eslint-disable import/no-named-as-default */
import React, { Component, Fragment, ReactNode } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import Pie from "../../Charts/Pie";
import TotalJobsPerSectorModal from "./TotalJobsPerSectorModal";
import TimeScaleDropDown from "./TimeScaleDropDown";
import AnalyticsWidgetLoading from "../../shared/loaders/AnalyticsWidgetLoading";
import colorPalette from "../colorPalette/colorPalette";
import {
  Wrapper,
  MobileLegend,
  ShowMore,
} from "../styles/total-jobs-per-sector-styles";
import { ChartTitle } from "../styles/employer-analytics-styles";

interface ITotalJobsPerSectorProps {
  totalJobsPerSector: {
    sectors?: Array<string>;
    values?: Array<number>;
  };
  totalJobsPerSectorWidgetLoader: boolean;
}

interface ITotalJobsPerSectorState {
  totalJobsPerSectorModalActive: boolean;
  timescaleDropdownActive: boolean;
  activeTimescale: {
    id: number;
    name: string;
  };
}

interface IFormatterProps {
  seriesIndex: number;
  w: {
    config: {
      series: () => void;
    };
  };
}

export class TotalJobsPerSector extends Component<
  ITotalJobsPerSectorProps,
  ITotalJobsPerSectorState
> {
  static propTypes = {
    totalJobsPerSector: PropTypes.shape({
      sectors: PropTypes.arrayOf(PropTypes.string),
      values: PropTypes.arrayOf(PropTypes.number),
    }),
    totalJobsPerSectorWidgetLoader: PropTypes.bool.isRequired,
  };

  static defaultProps = {
    totalJobsPerSector: {
      sectors: [],
      values: [],
    },
  };

  state = {
    totalJobsPerSectorModalActive: false,
    timescaleDropdownActive: false,
    activeTimescale: {
      id: 0,
      name: "All Time",
    },
  };

  toggleModal = (): void => {
    this.setState(({ totalJobsPerSectorModalActive }) => ({
      totalJobsPerSectorModalActive: !totalJobsPerSectorModalActive,
    }));
  };

  toggleDropdown = (): void => {
    this.setState(({ timescaleDropdownActive }) => ({
      timescaleDropdownActive: !timescaleDropdownActive,
    }));
  };

  selectTimescale = (timescale: { id: number; name: string }): void => {
    this.setState({
      activeTimescale: timescale,
    });
  };

  render(): ReactNode {
    const { totalJobsPerSector, totalJobsPerSectorWidgetLoader } = this.props;
    const {
      timescaleDropdownActive,
      activeTimescale,
      totalJobsPerSectorModalActive,
    } = this.state;

    const totalJobsPerSectorOptions = {
      series: totalJobsPerSector.values,
      options: {
        width: 330,
        colors: colorPalette(totalJobsPerSector.sectors.length),
        labels: totalJobsPerSector.sectors,
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: false,
        },
        plotOptions: {
          pie: {
            size: "65%",
            background: "transparent",
          },
        },
        tooltip: {
          enabled: true,
          style: {
            fontSize: "14px",
            fontFamily: "Komet, sans-serif",
          },
          x: {
            title: {
              formatter: (seriesName) => seriesName as unknown as string,
            },
          },
          y: {
            formatter: (value) => `${value as unknown as string} jobs`,
          },
        },
        chart: {
          dropShadow: {
            enabled: true,
            top: 0,
            left: 0,
            blur: 4,
            opacity: 0.1,
          },
        },
        responsive: [
          {
            breakpoint: 1199,
            options: {
              chart: {
                width: 375,
              },
              legend: {
                show: false,
              },
              dataLabels: {
                style: {
                  fontSize: "12px",
                },
              },
            },
          },
        ],
      },
    };

    const mobileLegendOptions = {
      series: totalJobsPerSector.values,
      options: {
        colors: colorPalette(totalJobsPerSector.sectors.length),
        labels: totalJobsPerSector.sectors,
        dataLabels: {
          enabled: false,
        },
        legend: {
          show: true,
          horizontalAlign: "left",
          formatter: (name: string, { seriesIndex, w }: IFormatterProps) =>
            `${name} - ${w.config.series[seriesIndex] as number}`,
          itemMargin: {
            horizontal: 4,
            vertical: 4,
          },
          position: "bottom",
          containerMargin: {
            top: 0,
          },
        },
        tooltip: {
          enabled: false,
        },
        chart: {
          width: "100%",
          height: 100,
        },
      },
    };

    const timescaleOptions = [
      { id: 0, name: "All Time" },
      { id: 7, name: "Last 7 days" },
      { id: 30, name: "Last 30 days" },
      { id: 90, name: "Last 90 days" },
    ];

    return (
      <Fragment>
        {totalJobsPerSectorModalActive && (
          <TotalJobsPerSectorModal toggleModal={this.toggleModal} />
        )}
        <Wrapper className="animated fadeIn delay-7">
          <AnalyticsWidgetLoading
            text="Updating..."
            active={totalJobsPerSectorWidgetLoader}
          />
          <ChartTitle
            marginBottom={20}
            data-test-id="total-jobs-per-sector-chart-title"
          >
            Total Jobs per Sector
            <TimeScaleDropDown
              toggleDropdown={this.toggleDropdown}
              dropdownActive={timescaleDropdownActive}
              selectTimescale={this.selectTimescale}
              activeTimescale={activeTimescale}
              options={timescaleOptions}
              type="JOBS_PER_SECTOR"
              testId="total-jobs-per-sector-chart-timescale-dropdown"
            />
          </ChartTitle>
          <Pie
            graphOptions={totalJobsPerSectorOptions}
            dataTestId="total-jobs-per-sector-chart"
          />
          {totalJobsPerSector.values.length > 0 && (
            <ShowMore
              data-test-id="total-jobs-per-sector-show-more"
              onClick={this.toggleModal}
            >
              show more
            </ShowMore>
          )}
          <MobileLegend>
            <Pie graphOptions={mobileLegendOptions} placeholderHidden />
          </MobileLegend>
        </Wrapper>
      </Fragment>
    );
  }
}

interface IReduxState {
  employerAnalytics: {
    chartsData: {
      totalJobsPerSector: unknown;
    };
    totalJobsPerSectorWidgetLoader: boolean;
  };
}

const mapStateToProps = (state: IReduxState) => ({
  totalJobsPerSector: state.employerAnalytics.chartsData.totalJobsPerSector,
  totalJobsPerSectorWidgetLoader:
    state.employerAnalytics.totalJobsPerSectorWidgetLoader,
});

export default connect(mapStateToProps, {})(TotalJobsPerSector);
